import React, { Children } from 'react';
import PropTypes from 'prop-types';
import AvatarIcon from '../AvatarIcon';
import MediaFrame from '../MediaFrame';
import { classNamesFor } from '../utils/classNames';
import isRtl from '../utils/isRtl';
var cx;
/**
 * The AvatarIconGroup component is used to show the users associated with a specific
 * event, uam, or who play a specific game.
 *
 * ## Composed of
 * - [AvatarIcon](../AvatarIcon/README.md)
 *
 * ## Related Components
 * - [AvatarIcon](../AvatarIcon/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <AvatarIconGroup size="small" maxFaces={4}>
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image1.webp"
 *       alt="So and so's image"
 *    />
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image2.webp"
 *       alt="So and so's image"
 *    />
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image3.webp"
 *       alt="So and so's image"
 *    />
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image4.webp"
 *       alt="So and so's image"
 *    />
 * </AvatarIconGroup>
 *
 *```
 * ## Style Hooks
 *
 * - **root** the root element
 * - **face** the face element
 * - **count** the count element
 *
 * ## Props
 */

var AvatarIconGroup = function AvatarIconGroup(props) {
  var qa = props.qa,
      className = props.className,
      maxFaces = props.maxFaces,
      size = props.size,
      children = props.children;

  var _AvatarIconGroup$getC = AvatarIconGroup.getClassName({
    size: size,
    className: className
  }),
      root = _AvatarIconGroup$getC.root,
      face = _AvatarIconGroup$getC.face,
      countClass = _AvatarIconGroup$getC.count;

  var allImages = Children.toArray(children);
  var images = allImages.slice(0, maxFaces).map(function (x) {
    return {
      src: x.props.src,
      alt: x.props.alt
    };
  });
  var shouldShowCount = allImages.length > maxFaces;
  var count = Math.min(allImages.length - maxFaces, 99);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("svg", {
    height: "0",
    width: "0"
  }, /*#__PURE__*/React.createElement("symbol", null, /*#__PURE__*/React.createElement("clipPath", {
    id: "ps-clip:crescent",
    viewBox: "0 0 100 100",
    clipPathUnits: "objectBoundingBox",
    transform: "scale(0.01 0.01)"
  }, /*#__PURE__*/React.createElement("path", {
    transform: isRtl() ? 'scale(-1 1) translate(-100 0)' : '',
    d: "M76 50c0-12.508 4.264-24.013 11.402-33.167C78.243 6.512 64.885 0 50 0 22.386 0 0 22.386 0 50s22.386 50 50 50c14.885 0 28.243-6.512 37.402-16.833C80.264 74.013 76 62.508 76 50zM130 0c27.614 0 50 22.386 50 50s-22.386 50-50 50"
  })))), /*#__PURE__*/React.createElement("span", {
    "data-qa": qa,
    className: root
  }, images.map(function (_ref, i) {
    var src = _ref.src,
        alt = _ref.alt;
    return /*#__PURE__*/React.createElement(AvatarIcon, {
      qa: "".concat(qa, "#avatarIcon").concat(i),
      key: alt,
      className: face,
      src: src,
      alt: alt,
      size: size
    });
  }), shouldShowCount && /*#__PURE__*/React.createElement(MediaFrame, {
    qa: "".concat(qa, "#count"),
    className: countClass,
    aspectRatio: "1:1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "psw-center"
  }, "+".concat(count)))));
};

AvatarIconGroup.getClassName = function (_ref2) {
  var size = _ref2.size,
      className = _ref2.className;
  var imageSize = {
    xs: 'xs',
    small: 's',
    medium: 'm'
  };
  return {
    root: cx('psw-avatar-icon-group', "psw-avatar-icon-group--".concat(size), className),
    face: cx('psw-avatar-icon-group-face', 'psw-round', "psw-l-avatar-icon-".concat(imageSize[size])),
    count: cx('psw-avatar-icon-group-count', 'psw-round', 'psw-bg-3', "psw-l-avatar-icon-".concat(imageSize[size]))
  };
};

AvatarIconGroup.useClassNames = function (x) {
  AvatarIconGroup.classNames = x;
};

AvatarIconGroup.defaultProps = {
  qa: '',
  className: '',
  size: 'medium',
  maxFaces: 4
};
AvatarIconGroup.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * The size of avatars to use
   */
  size: PropTypes.oneOf(['xs', 'small', 'medium']),

  /**
   * The number of faces to show before showing count
   */
  maxFaces: PropTypes.number,

  /**
   * Used to pass in the player avatar images. See usage above.
   */
  children: PropTypes.node.isRequired
};
cx = classNamesFor(AvatarIconGroup);
export default AvatarIconGroup;